/**
 * A small status pill. Text first, colour second.
 *
 * The tone is a reinforcement, never the message: every badge renders its label
 * as words, so a reader who cannot tell teal from amber loses no information.
 * That is also why there is no icon-only variant.
 *
 * Built with the repo's inline pill convention rather than `ui/badge.jsx` — that
 * shadcn primitive exists in the tree but is imported nowhere, and its first use
 * would set a precedent this slice has no reason to set.
 *
 * WHY THE TINTS ARE THE `-100` STEP AND NOT `-50` (DEP-L1). `index.css` carries a
 * light-mode "solid pill" system: a set of `html[data-theme="light"]` rules that
 * repaint any element matching a `border-<hue>-200` + `bg-<hue>-50` + pill-shape
 * signature into a solid brand colour with `!important`. Two of those rules
 * matched this badge — `index.css:409` (`.border-emerald-200.bg-emerald-50
 * .rounded-full`) and `index.css:473` (the same plus `.inline-flex
 * .items-center.text-emerald-800`) — and repainted the `positive` tone to a
 * solid `#639922` with white text. Measured in a real browser under
 * `data-theme="light"`: 3.44:1, below the 4.5:1 that WCAG AA requires for text
 * this size (11px at weight 600 is not "large text", which starts at 18.66px
 * bold). The `bad` and `info` tones were in the same position, at 3.93:1 and
 * 3.59:1.
 *
 * THE GLOBAL RULES ARE NOT THIS COMPONENT'S TO CHANGE. That signature is shared:
 * 28 production sites pair `border-emerald-200` with `bg-emerald-50`, and 8 of
 * them carry the exact `text-emerald-800` variant, among them
 * `PortfolioMapSection`, `listings/ListingStatusBadge`, `nachweise/
 * RequirementsTable`, `unit/Badge` and `automations/automationLabels`. Editing
 * the rule to fix one badge would repaint all of them, which is a design
 * decision about the whole light theme rather than an accessibility fix here.
 *
 * SO THE BADGE OWNS ITS OWN APPEARANCE INSTEAD, by not matching. Every one of
 * those rules requires a `bg-<hue>-50`; no rule targets the `-100` step, and
 * none targets `border-<hue>-200` or `text-<hue>-800` on their own. Moving the
 * four tinted tones one step deeper therefore takes the badge out of the solid
 * system and restores the pale pill its own class list always described — the
 * appearance this file was written for before the global rules overrode it.
 * Measured after the change: positive 6.78:1, warn 6.37:1, bad 6.68:1, info
 * 6.59:1 in light; 9.68:1 to 12.00:1 in dark, which the change does not touch.
 *
 * ALL FOUR TINTED TONES MOVE TOGETHER, including `warn`, which passed AA at
 * 7.95:1 as a solid pill. Fixing only the failing ones would leave one badge
 * family speaking two visual languages — a solid amber pill beside a pale green
 * one — and the point of a shared component is that a consumer never has to
 * know which tone got which treatment.
 *
 * NOTHING SEMANTIC MOVES. The tone names, the mapping from deposit state to
 * tone, and the labels all still live in `depositsLabels.js`. This file changed
 * colour classes and nothing else.
 */
import React from "react";

const TONES = {
  positive:
    "border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  warn: "border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  bad: "border-rose-200 bg-rose-100 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300",
  info: "border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300",
  // Already theme-token based, already AA in both themes (16.33:1 and 5.34:1
  // measured in light), and matched by none of the light-mode pill rules.
  neutral: "border-border bg-muted text-foreground",
  muted: "border-border bg-transparent text-muted-foreground",
};

export default function DepositBadge({ tone = "neutral", children, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        TONES[tone] || TONES.neutral
      }`}
    >
      {children}
    </span>
  );
}
